import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Receipt, 
  Save,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Search
} from 'lucide-react';
import { receiptsAPI, clientsAPI } from '../services/api';

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
}

interface ReceiptData {
  _id: string;
  receiptNumber: string;
  receiptDate: string;
  invoice?: string;
  invoiceNumber: string;
  client: Client | string;
  amount: number;
  amountInWords: string;
  subtotal?: number;
  taxableAmount?: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  paymentMode: string;
  paymentDetails: {
    referenceNumber?: string;
    bankName?: string;
    chequeNumber?: string;
    transactionDate?: string;
  };
  notes?: string;
  isManual: boolean;
  tdsDeducted?: number;
  tdsPercentage?: number;
}

const EditableReceiptDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const [formData, setFormData] = useState({
    receiptNumber: '',
    receiptDate: '',
    invoiceNumber: '',
    client: '',
    amount: 0,
    paymentMode: 'Bank Transfer',
    referenceNumber: '',
    bankName: '',
    chequeNumber: '',
    transactionDate: '',
    notes: '',
    tdsPercentage: 0,
    tdsDeducted: 0,
    subtotal: 0,
    taxableAmount: 0,
    cgstRate: 0,
    sgstRate: 0,
    igstRate: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0
  });

  const fetchReceipt = useCallback(async () => {
    if (!id) return;
    
    try {
      const [receiptRes, clientsRes] = await Promise.all([
        receiptsAPI.getById(id),
        clientsAPI.getAll({ limit: 1000 })
      ]);
      
      const receiptData = receiptRes.data;
      setReceipt(receiptData);
      setClients(clientsRes.data.clients);

      // Set selected client
      const client = typeof receiptData.client === 'object' ? receiptData.client : 
        clientsRes.data.clients.find((c: Client) => c._id === receiptData.client);
      setSelectedClient(client || null);

      // Populate form with receipt data
      setFormData({
        receiptNumber: receiptData.receiptNumber,
        receiptDate: receiptData.receiptDate.split('T')[0],
        invoiceNumber: receiptData.invoiceNumber || '',
        client: typeof receiptData.client === 'object' ? receiptData.client._id : receiptData.client,
        amount: receiptData.amount,
        paymentMode: receiptData.paymentMode || 'Bank Transfer',
        referenceNumber: receiptData.paymentDetails?.referenceNumber || '',
        bankName: receiptData.paymentDetails?.bankName || '',
        chequeNumber: receiptData.paymentDetails?.chequeNumber || '',
        transactionDate: receiptData.paymentDetails?.transactionDate?.split('T')[0] || receiptData.receiptDate.split('T')[0],
        notes: receiptData.notes || '',
        tdsPercentage: receiptData.tdsPercentage || 0,
        tdsDeducted: receiptData.tdsDeducted || 0,
        subtotal: receiptData.subtotal || 0,
        taxableAmount: receiptData.taxableAmount || 0,
        cgstRate: receiptData.cgstRate || 0,
        sgstRate: receiptData.sgstRate || 0,
        igstRate: receiptData.igstRate || 0,
        cgstAmount: receiptData.cgstAmount || 0,
        sgstAmount: receiptData.sgstAmount || 0,
        igstAmount: receiptData.igstAmount || 0
      });
    } catch (error) {
      console.error('Error fetching receipt:', error);
      setMessage({ type: 'error', text: 'Failed to load receipt' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReceipt();
  }, [fetchReceipt]);

  const handleClientSelect = (clientId: string) => {
    const client = clients.find(c => c._id === clientId);
    setSelectedClient(client || null);
    setFormData(prev => ({ ...prev, client: clientId }));
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate TDS deducted when percentage, subtotal, or amount changes
      // TDS should be calculated on subtotal (gross amount) not on total amount (which includes tax)
      if (field === 'tdsPercentage' || field === 'amount' || field === 'subtotal') {
        const tdsPercentage = field === 'tdsPercentage' ? value as number : newData.tdsPercentage;
        // Use subtotal if available and > 0, otherwise use amount (for manual receipts without tax info)
        const baseAmount = (newData.subtotal > 0) ? newData.subtotal : newData.amount;
        const tdsDeducted = (baseAmount * tdsPercentage) / 100;
        newData.tdsDeducted = Math.round(tdsDeducted * 100) / 100;
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receipt) return;
    
    setSaving(true);
    setMessage(null);

    try {
      if (!formData.client) {
        setMessage({ type: 'error', text: 'Please select a client' });
        setSaving(false);
        return;
      }

      if (!formData.amount || formData.amount <= 0) {
        setMessage({ type: 'error', text: 'Please enter a valid amount' });
        setSaving(false);
        return;
      }

      const paymentDetails: any = {
        transactionDate: formData.transactionDate || formData.receiptDate
      };

      if (formData.referenceNumber) {
        paymentDetails.referenceNumber = formData.referenceNumber;
      }

      if (formData.bankName) {
        paymentDetails.bankName = formData.bankName;
      }

      if (formData.chequeNumber) {
        paymentDetails.chequeNumber = formData.chequeNumber;
      }

      // Round off amount
      const roundedAmount = Math.round(formData.amount);
      
      const updateData = {
        receiptNumber: formData.receiptNumber,
        receiptDate: formData.receiptDate,
        invoiceNumber: formData.invoiceNumber,
        client: formData.client,
        amount: roundedAmount,
        paymentMode: formData.paymentMode,
        paymentDetails,
        notes: formData.notes,
        tdsPercentage: formData.tdsPercentage,
        tdsDeducted: formData.tdsDeducted,
        amountInWords: numberToWords(Math.round(formData.tdsDeducted > 0 ? roundedAmount - formData.tdsDeducted : roundedAmount)),
        subtotal: formData.subtotal,
        taxableAmount: formData.taxableAmount,
        cgstRate: formData.cgstRate,
        sgstRate: formData.sgstRate,
        igstRate: formData.igstRate,
        cgstAmount: formData.cgstAmount,
        sgstAmount: formData.sgstAmount,
        igstAmount: formData.igstAmount
      };

      await receiptsAPI.update(receipt._id, updateData);
      setMessage({ type: 'success', text: 'Receipt updated successfully!' });
      
      // Refresh receipt data
      setTimeout(() => {
        fetchReceipt();
      }, 1000);
    } catch (error) {
      console.error('Error updating receipt:', error);
      setMessage({ type: 'error', text: 'Failed to update receipt' });
    } finally {
      setSaving(false);
    }
  };

  const numberToWords = (num: number) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    
    function convertHundreds(n: number): string {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result.trim();
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    }
    
    let result = '';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = num % 1000;
    
    if (crore > 0) {
      result += convertHundreds(crore) + ' Crore ';
    }
    if (lakh > 0) {
      result += convertHundreds(lakh) + ' Lakh ';
    }
    if (thousand > 0) {
      result += convertHundreds(thousand) + ' Thousand ';
    }
    if (hundred > 0) {
      result += convertHundreds(hundred) + ' ';
    }
    
    return result.trim() + ' Rupees Only';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="text-center py-8">
        <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Receipt not found</p>
      </div>
    );
  }

  const netAmount = Math.round(formData.tdsDeducted > 0 ? formData.amount - formData.tdsDeducted : formData.amount);
  const hasTaxInfo = (formData.subtotal > 0 && (formData.cgstAmount > 0 || formData.sgstAmount > 0 || formData.igstAmount > 0));

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/revised')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-3">
            <Receipt className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Receipt: {receipt.receiptNumber}</h1>
              <p className="text-gray-600">Revise receipt details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Receipt Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Receipt Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="form-label">Receipt Number *</label>
              <input
                type="text"
                value={formData.receiptNumber}
                onChange={(e) => handleInputChange('receiptNumber', e.target.value)}
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Receipt Date *</label>
              <input
                type="date"
                value={formData.receiptDate}
                onChange={(e) => handleInputChange('receiptDate', e.target.value)}
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                className="form-input"
                placeholder="Enter invoice number or N/A"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="form-label">Select Client *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={formData.client}
                onChange={(e) => handleClientSelect(e.target.value)}
                className="form-input pl-10"
                required
              >
                <option value="">Choose a client...</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.clientName} - {client.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-gray-50 rounded-lg"
            >
              <div className="text-sm text-gray-600 space-y-1">
                <p className="font-medium text-gray-900">{selectedClient.clientName}</p>
                <p>{selectedClient.email} • {selectedClient.contactNumber}</p>
                <p>{selectedClient.address.street}, {selectedClient.address.city}, {selectedClient.address.state} - {selectedClient.address.pincode}</p>
                {selectedClient.gstin && <p>GSTIN: {selectedClient.gstin}</p>}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Payment Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Gross Amount *</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                className="form-input"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="form-label">Payment Mode *</label>
              <select
                value={formData.paymentMode}
                onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                className="form-input"
                required
              >
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
                <option value="IMPS">IMPS</option>
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Card">Card</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="form-label">Reference Number</label>
              <input
                type="text"
                value={formData.referenceNumber}
                onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                className="form-input"
                placeholder="Transaction/Reference number"
              />
            </div>

            <div>
              <label className="form-label">Transaction Date</label>
              <input
                type="date"
                value={formData.transactionDate}
                onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">Bank Name</label>
              <input
                type="text"
                value={formData.bankName}
                onChange={(e) => handleInputChange('bankName', e.target.value)}
                className="form-input"
                placeholder="Bank name (if applicable)"
              />
            </div>

            <div>
              <label className="form-label">Cheque Number</label>
              <input
                type="text"
                value={formData.chequeNumber}
                onChange={(e) => handleInputChange('chequeNumber', e.target.value)}
                className="form-input"
                placeholder="Cheque number (if applicable)"
              />
            </div>
          </div>
        </motion.div>

        {/* Tax and TDS Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tax & TDS Details</h2>
          
          {hasTaxInfo && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-semibold text-gray-900 mb-3">Tax Breakdown:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Subtotal</label>
                  <input
                    type="number"
                    value={formData.subtotal}
                    onChange={(e) => handleInputChange('subtotal', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="form-label">Taxable Amount</label>
                  <input
                    type="number"
                    value={formData.taxableAmount}
                    onChange={(e) => handleInputChange('taxableAmount', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    step="0.01"
                  />
                </div>
                {formData.cgstRate > 0 && (
                  <>
                    <div>
                      <label className="form-label">CGST Rate (%)</label>
                      <input
                        type="number"
                        value={formData.cgstRate}
                        onChange={(e) => handleInputChange('cgstRate', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="form-label">CGST Amount</label>
                      <input
                        type="number"
                        value={formData.cgstAmount}
                        onChange={(e) => handleInputChange('cgstAmount', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </>
                )}
                {formData.sgstRate > 0 && (
                  <>
                    <div>
                      <label className="form-label">SGST Rate (%)</label>
                      <input
                        type="number"
                        value={formData.sgstRate}
                        onChange={(e) => handleInputChange('sgstRate', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="form-label">SGST Amount</label>
                      <input
                        type="number"
                        value={formData.sgstAmount}
                        onChange={(e) => handleInputChange('sgstAmount', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </>
                )}
                {formData.igstRate > 0 && (
                  <>
                    <div>
                      <label className="form-label">IGST Rate (%)</label>
                      <input
                        type="number"
                        value={formData.igstRate}
                        onChange={(e) => handleInputChange('igstRate', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="form-label">IGST Amount</label>
                      <input
                        type="number"
                        value={formData.igstAmount}
                        onChange={(e) => handleInputChange('igstAmount', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">TDS Percentage (%)</label>
              <input
                type="number"
                value={formData.tdsPercentage}
                onChange={(e) => handleInputChange('tdsPercentage', parseFloat(e.target.value) || 0)}
                className="form-input"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 10"
              />
            </div>

            <div>
              <label className="form-label">TDS Deducted (Auto-calculated)</label>
              <input
                type="text"
                value={formatCurrency(formData.tdsDeducted)}
                readOnly
                className="form-input bg-gray-50 text-gray-600"
              />
            </div>
          </div>

          <div className="mt-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">
                {formData.tdsDeducted > 0 ? 'Net Amount Received:' : 'Amount Received:'}
              </span>
              <span className="text-2xl font-bold text-primary-600">
                {formatCurrency(netAmount)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <label className="form-label">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="form-input"
            rows={4}
            placeholder="Additional notes..."
          />
        </motion.div>

        {/* Form Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end space-x-4"
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/revised')}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default EditableReceiptDetails;

